import React, { useCallback, useMemo } from 'react';
import { Card } from '../Card/Card';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import './styles/userDetails.scss';
import { formatWalletBalance } from '@/utilities/helpers/formatWalletBalance';
import { BRANDS, PLATFORMS } from '@/utilities/constants/game';
import PlusIcon from '@/features/Icons/PlusIcon';
import useDepositFundsAndReloadBalance from '@/unified-modules/wallet/helpers/useAddFundsAndReloadBalance';
import { sendXsellDepositMessage } from '@/utilities/helpers/wrapperHelpers';
import { SHOWOFF_ELEMENTS } from '@/features/Showoff';
import {
  TRACK_USER_ACTION_EVENT,
  useTrackUserAction,
} from '@/utilities/logger/amplitude/tracking';

const formatDateTime = (value: Date | string | null): string => {
  if (!value) return '--';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '--';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const UserDetails: React.FC = () => {
  const platform = useStore((s) => s.runtime.platform);
  const deviceType = useStore((s) => s.runtime.deviceType);
  const isMohegansun = useStore((s) => s.runtime.brand) === BRANDS.MOHEGANSUN;
  const spinInProgress = useStore((s) => s.gameWindowContext.spinInProgress);
  const { showDepositButton, depositLink } = useStore(
    useShallow((s) => ({
      showDepositButton: s.commonUIContext.showDepositButton,
      depositLink: s.commonUIContext.depositLink,
    })),
  );
  const sessionStartTime = useStore(
    (s) => s.userSessionContext.sessionStartTime,
  );
  const sessionTime = useStore((s) => s.sessionTime);
  const data = useStore((s) => s.userContext.data);
  const balanceDetails = useStore((s) => s.gameWindowContext.balanceDetails);

  const depositFundsAndReloadBalance = useDepositFundsAndReloadBalance();
  const { trackUserAction } = useTrackUserAction();

  const accountSettings = data?.accountSettings;

  const username = accountSettings?.username ?? '--';
  const lastLoginValue =
    accountSettings?.lastLoginDate ?? sessionStartTime ?? null;
  const formattedLastLogin = useMemo(
    () => formatDateTime(lastLoginValue),
    [lastLoginValue],
  );
  const formatBalanceValue = (value?: number | null) =>
    formatWalletBalance(value ?? undefined) ?? '--';
  const casinoBonusesTotal =
    (balanceDetails?.casinoBonuses ?? 0) + (balanceDetails?.freeBets ?? 0);
  // When the in-game (FGP) balance is active, show its eligibility-filtered
  // overlay for all four rows instead of the plain wallet values (mirrors v2 MyAccount).
  const inGameBalanceActive = balanceDetails?.inGameBalanceActive;
  const playableBalanceValue = inGameBalanceActive
    ? balanceDetails?.inGamePlayableBalance
    : balanceDetails?.playableBalance;
  const casinoBonusesValue = inGameBalanceActive
    ? balanceDetails?.inGameEligibleBonus
    : balanceDetails
      ? casinoBonusesTotal
      : undefined;
  const depositsValue = inGameBalanceActive
    ? balanceDetails?.inGameDeposits
    : balanceDetails?.casinoDeposits;
  const sharedWinningsValue = inGameBalanceActive
    ? balanceDetails?.inGameSharedWinnings
    : balanceDetails?.sharedWinnings;
  const formattedPlayableBalance = formatBalanceValue(playableBalanceValue);
  const formattedCasinoDeposits = formatBalanceValue(depositsValue);
  const formattedCasinoBonuses = formatBalanceValue(casinoBonusesValue);
  const formattedSharedWinnings = formatBalanceValue(sharedWinningsValue);
  const canDeposit = showDepositButton && !spinInProgress;
  const depositButtonContent = isMohegansun ? (
    <PlusIcon
      isMobile={deviceType !== 'desktop'}
      size={34}
      className="plus"
      dataTestId="deposit-plus"
    />
  ) : (
    'Deposit'
  );

  const handleDepositButton = useCallback(async () => {
    if (!canDeposit) return;
    trackUserAction({
      event: TRACK_USER_ACTION_EVENT.IN_GAME_CTA_CLICKED,
      module: 'GW',
      zone: 'DASHBOARD',
      linkUrl: depositLink,
      linkText: 'deposit',
    });
    if (platform === PLATFORMS.Desktop) {
      await depositFundsAndReloadBalance();
    }
    sendXsellDepositMessage();
  }, [
    canDeposit,
    platform,
    depositFundsAndReloadBalance,
    trackUserAction,
    depositLink,
  ]);

  return (
    <Card
      action={() => undefined}
      className="user-details"
      data-showoff={SHOWOFF_ELEMENTS.MY_ACCOUNT_CARD}
      data-showoff-badge="false"
    >
      <div className="user-details__content">
        <div
          className="user-details__title"
          data-testid="user-details-username"
        >
          {username}
        </div>
        <div className="user-details__grid">
          <div className="user-details__row">
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="last-login-label"
              >
                Last Login
              </span>
              <span
                className="user-details__value"
                data-testid="last-login-value"
              >
                {formattedLastLogin}
              </span>
            </div>
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="session-time-label"
              >
                Session Time
              </span>
              <span
                className="user-details__value"
                data-testid="session-time-value"
              >
                {sessionTime}
              </span>
            </div>
          </div>
          <div className="user-details__row">
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="playable-balance-label"
              >
                Playable balance
              </span>
              <span
                className="user-details__value"
                data-testid="playable-balance-value"
              >
                {formattedPlayableBalance}
              </span>
            </div>
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="deposits-label"
              >
                Deposits
              </span>
              <span
                className="user-details__value"
                data-testid="deposits-value"
              >
                {formattedCasinoDeposits}
              </span>
            </div>
          </div>
          <div className="user-details__row">
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="casino-bonuses-label"
              >
                {inGameBalanceActive
                  ? 'Eligible Casino Bonuses'
                  : 'Casino Bonuses'}
              </span>
              <span
                className="user-details__value"
                data-testid="casino-bonuses-value"
              >
                {formattedCasinoBonuses}
              </span>
            </div>
            <div className="user-details__item">
              <span
                className="user-details__label"
                data-testid="shared-winnings-label"
              >
                Shared Winnings
              </span>
              <span
                className="user-details__value"
                data-testid="shared-winnings-value"
              >
                {formattedSharedWinnings}
              </span>
            </div>
          </div>
        </div>
        {showDepositButton && (
          <div className="user-details__actions">
            {platform === PLATFORMS.Desktop ? (
              <button
                className="user-details__deposit-button"
                onClick={handleDepositButton}
                disabled={!canDeposit}
              >
                {depositButtonContent}
              </button>
            ) : (
              <a
                className={`user-details__deposit-button ${
                  canDeposit ? '' : 'disabled'
                }`}
                href={canDeposit ? depositLink : undefined}
                rel="noreferrer"
                target="_blank"
                onClick={handleDepositButton}
              >
                {depositButtonContent}
              </a>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
